import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  Divider,
  Stack,
  Avatar,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  IconButton
} from '@chakra-ui/react';
import { FaEnvelope, FaPhone, FaIdBadge } from 'react-icons/fa';
import {  BadgeCheck, BadgeInfo, BadgeX, Calendar, Clock, Download } from "lucide-react";
import PdfIcon from "assets/svg/pdf.png";
import WordIcon from "assets/svg/word.png";
import ImageIcon from "assets/svg/jpg.png";
import NoDataFound from 'components/NoDataGraphics/NoDataFound';
import DocumentViewer from 'components/DocumentViewer/DocumentViewer';
import ReusableAvatar from 'components/Avatar/ReusableAvatar';
import { formatDate } from "utils/dateFormatter";


const DocumentIcon = ({ type }) => {
  switch (type) {
    case "pdf":
      return <img src={PdfIcon} alt="PDF Icon" width={24} height={24} />;
    case "docx":
      return <img src={WordIcon} alt="Word Icon" width={24} height={24} />;
    case "jpeg":
      return <img src={ImageIcon} alt="Image Icon" width={24} height={24} />;
    default:
      return null;
  }
};

const ViewProfile = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null); // Change to hold a single document

  const downloadDocument = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!user) return null;

  const handleDocumentClick = (doc) => {
    setCurrentDocument(doc); // Set the clicked document
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentDocument(null); // Reset current document on close
  };

  return (
    <Flex direction="column" p={8} width="100%">
        
      {/* Header with Avatar and Basic Info */}
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center">
          <ReusableAvatar
            name={`${user.first_name} ${user.last_name}`} 
           
          />
          <Box>
            <Text fontSize="2xl" fontWeight="bold" display="flex" alignItems="center">
              {user.firstName} {user.lastName}
              {user.status === 'Active' && (
                <>
                  <Icon as={BadgeCheck} size={12} color="green.500" stroke="white" ml={1} fill="green.500" />
                  <Text color="green.500" fontWeight="xs" fontSize="13px"> Active</Text>
                </>
              )}
              {user.status === 'Inactive' && (
                <>
                  <Icon as={BadgeInfo} size={12} color="yellow.500" stroke="white" ml={1} fill="yellow.500" />
                  <Text color="yellow.500" fontWeight="xs" fontSize="13px" display="flex" alignItems="center"> Inactive</Text>
                </>
              )}
              {user.status === 'Suspended' && (
                <>
                  <Icon as={BadgeX} size={12} color="red.500" stroke="white" ml={1} fill="red.500" />
                  <Text color="red.500" fontWeight="xs" fontSize="13px" display="flex" alignItems="center"> Suspended</Text>
                </>
              )}
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {user.user_type} • {user.district}
            </Text>
          </Box>
        </Flex>
      </Flex>

      <Flex mt={2} alignItems="center">
        <Icon as={Calendar} size={16} color="gray.500" mr={1} />
        <Text fontSize="sm" color="gray.500" mr={4}>
          Date Added: {formatDate(user.created_at)}
        </Text>
        <Icon as={Clock} size={16} color="gray.500" mr={1} />
        <Text fontSize="sm" color="gray.500">
          Last Active: {user.lastActive}
        </Text>
      </Flex>

      <Divider my={6} />

      {/* User Information */}
      <Box mb={6}>
        <Stack spacing={4}>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}>
            <Icon as={FaIdBadge} mr={2} color="gray.400" />
            <Text fontWeight="500" color="gray.500" fontSize="sm">{user.username}</Text>
          </Flex>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}>
            <Icon as={FaEnvelope} mr={2} color="gray.400" />
            <Text fontWeight="500" color="gray.500" fontSize="sm">{user.email}</Text>
          </Flex>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}>
            <Icon as={FaPhone} mr={2} color="gray.400" />
            <Text fontWeight="500" color="gray.500" fontSize="sm">{user.phone_number}</Text>
          </Flex>
        </Stack>
      </Box>
      <Divider my={6} />

      {/* Documents Section */}
      <Box mb={6}>
        <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>
          Documents
        </Text>
        {user.documents && user.documents.length > 0 ? (
          <Stack spacing={2}>
            {user.documents.map((doc, index) => (
              <Tooltip 
                label="Click to open document" 
                hasArrow 
                placement="top" 
                bg="linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)"
                color="white" // Text color
                key={index}
              >
                <Box 
                  p={2} 
                  border="1px" 
                  borderColor="gray.200" 
                  borderRadius="md" 
                  display="flex" 
                  alignItems="center"
                  _hover={{ cursor: 'pointer', bg: 'gray.100' }} // Change background on hover
                  onClick={() => handleDocumentClick(doc)} // Pass the clicked document
                >
                  <DocumentIcon type={doc.type} />
                  <Box ml={2}>
                    <Text fontWeight="bold">{doc.title}</Text>
                    <Text color="gray.500">Date Uploaded: {doc.dateUploaded}</Text>
                  </Box>
                </Box>
              </Tooltip>
            ))}
          </Stack>
        ) : (
          <NoDataFound
            message="No documents uploaded"
            width="100px" 
            height="100px" 
            fontSize="sm" 
            fontWeight="bold" 
          />
        )}
      </Box>
      <Modal isOpen={isOpen} onClose={closeModal}>
  <ModalOverlay />
  <ModalContent
    maxWidth="500px"
    maxHeight="80vh"
    overflowY="auto"
  >
    <ModalHeader fontSize={"16px"}>Document Viewer</ModalHeader>
    <ModalCloseButton />
    <ModalBody>
      {currentDocument ? (
        <>
          <DocumentViewer documents={[currentDocument]} />
          <IconButton
            aria-label="Download Document"
            icon={<Download />}
            onClick={() => downloadDocument(currentDocument.url, currentDocument.title)}
            bgGradient="linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)" // Gradient colors
            color="white"
            position="sticky" 
            bottom="20px"
            left="20px"
            zIndex="10"
            _hover={{ bgGradient: "linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)" }} // Hover effect
            boxShadow="md"
            borderRadius="full"
            size="lg" 
          />
        </>
      ) : (
        <NoDataFound message="No document to display." />
      )}
    </ModalBody>
  </ModalContent>
</Modal>
    </Flex>
  );
};

export default ViewProfile;
